import { KText } from '@/ui-library/components/text';
import { Box, Flex, Icon } from '@chakra-ui/react';
import { memo, useCallback, useState, useRef, useEffect } from 'react';
import { AIButton1 } from '@/components/candidate-job-slider-v1/icons/aiButton1';
import ReactMarkdown from 'react-markdown';

export const AISummary = memo(function AISummary({
  summary,
}: {
  summary: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isContentOverflowing, setIsContentOverflowing] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);

  const toggleFullContent = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Measure content height to determine if truncation is needed
  useEffect(() => {
    if (contentRef.current) {
      const scrollHeight = contentRef.current.scrollHeight;
      setContentHeight(scrollHeight);
      setIsContentOverflowing(scrollHeight > 70);
    }
  }, [summary]);

  const completedContent = useCallback(() => {
    return (
      <Flex
        gap="14px"
        alignItems="flex-start"
        justifyContent="space-between"
        width="100%"
        className="chat-message-bot-content"
      >
        <Flex
          gap="4px"
          alignItems="flex-start"
          flex="1"
          onClick={
            isContentOverflowing
              ? (e: React.MouseEvent) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleFullContent();
                }
              : undefined
          }
          cursor={isContentOverflowing ? 'pointer' : 'default'}
        >
          <Box position="relative">
            {/* Hidden full content for measuring height */}
            <Box
              ref={contentRef}
              visibility="hidden"
              position="absolute"
              top="0"
              left="0"
              right="0"
              pointerEvents="none"
            >
              <KText
                size="sm-regular"
                color="secondary-700"
                lineHeight="24px"
                fontSize="0.85rem"
              >
                <ReactMarkdown
                  components={{
                    p: (() => {
                      let isFirst = true;
                      return ({ children }) => {
                        const showIcon = isFirst;
                        isFirst = false;
                        return (
                          <p>
                            {showIcon && (
                              <Icon
                                as={AIButton1}
                                h="22px"
                                w="22px"
                                flexShrink={0}
                                mr="5px"
                              />
                            )}
                            {children}
                          </p>
                        );
                      };
                    })(),
                  }}
                >
                  {summary}
                </ReactMarkdown>
              </KText>
            </Box>

            {/* Animated content container */}
            <Box
              position="relative"
              overflow="hidden"
              maxHeight={isExpanded ? `${contentHeight}px` : '45px'}
              transition="max-height 0.3s ease-in-out"
              style={{ willChange: 'max-height' }}
            >
              <KText size="sm-regular" color="secondary-700" lineHeight="24px">
                <ReactMarkdown
                  components={{
                    p: (() => {
                      let isFirst = true;
                      return ({ children }) => {
                        const showIcon = isFirst;
                        isFirst = false;
                        return (
                          <p>
                            {showIcon && (
                              <Icon
                                as={AIButton1}
                                h="18px"
                                w="18px"
                                flexShrink={0}
                                mr="5px"
                              />
                            )}
                            {children}
                          </p>
                        );
                      };
                    })(),
                  }}
                >
                  {summary}
                </ReactMarkdown>
              </KText>

              {/* Custom ellipsis overlay - fades out when expanded */}
              {isContentOverflowing && (
                <Box
                  position="absolute"
                  bottom="-3px"
                  right="0"
                  bg="linear-gradient(to right, transparent, white 30%)"
                  pl="30px"
                  pr="30px"
                  opacity={isExpanded ? 0 : 1}
                  transition="opacity 0.2s ease-in-out"
                  pointerEvents={isExpanded ? 'none' : 'auto'}
                  className="ai-search-summary-ellipsis"
                >
                  <KText
                    size="sm-medium !important"
                    color="primary-500 !important"
                    fontWeight="550 !important"
                    lineHeight="24px"
                  >
                    ... Read all
                  </KText>
                </Box>
              )}
            </Box>
          </Box>
        </Flex>
      </Flex>
    );
  }, [
    toggleFullContent,
    summary,
    isExpanded,
    isContentOverflowing,
    contentHeight,
  ]);

  return <Box>{completedContent()}</Box>;
});
